import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Wallet, Gift, Zap } from 'lucide-react';
import { brokerRedirectUrl, track } from '../lib/api';
import { trackPixelEvent } from '../lib/pixels';

/**
 * Recommended Brokers section.
 *
 * Supports up to TWO brokers — Pocket Option (primary) + Quotex (secondary).
 * Each broker has an independent `*_enabled` visibility toggle in admin settings.
 * Click routes through /api/broker/redirect (PO) or /api/broker/redirect/quotex
 * so a server-generated click_id is attached for S2S postback attribution.
 */
export default function BrokerCta({ settings, geo }) {
  if (!settings) return null;

  const brokers = [];

  if (settings.broker_enabled && settings.broker_link) {
    brokers.push({
      key: 'po',
      name: settings.broker_name || 'Pocket Option',
      tagline: settings.broker_tagline || 'Our official trading partner',
      ctaLabel: settings.broker_cta_label || 'Open Free Account',
      payout: settings.broker_payout || '',
      minDeposit: settings.broker_min_deposit || '',
      bonus: settings.broker_bonus || '',
      logo: settings.broker_logo_url || '',
      redirectUrl: brokerRedirectUrl('broker_section', 'po'),
      sourceTag: 'broker_section',
      accent: 'gold',
    });
  }

  if (settings.quotex_enabled && settings.quotex_broker_link) {
    brokers.push({
      key: 'quotex',
      name: settings.quotex_broker_name || 'Quotex',
      tagline: settings.quotex_broker_tagline || 'Alternate official trading partner',
      ctaLabel: settings.quotex_broker_cta_label || 'Open Quotex Account',
      payout: settings.quotex_payout_label || '',
      minDeposit: settings.quotex_min_deposit || '',
      bonus: settings.quotex_broker_bonus || '',
      logo: settings.quotex_broker_logo_url || '',
      redirectUrl: brokerRedirectUrl('broker_section_quotex', 'quotex'),
      sourceTag: 'broker_section_quotex',
      accent: 'blue',
    });
  }

  if (brokers.length === 0) return null;

  function handleClick(sourceTag) {
    track({
      type: 'broker_click',
      country_code: geo?.country_code,
      country_name: geo?.country_name,
      source: sourceTag,
    });
    trackPixelEvent('Lead', { source: sourceTag });
  }

  return (
    <section
      id="broker"
      data-testid="broker-section"
      className="section-pad bg-[hsl(0_0%_4%)] border-t border-b border-border relative overflow-hidden"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            'radial-gradient(60% 60% at 80% 20%, rgba(245,185,66,0.08), transparent 70%), radial-gradient(60% 50% at 10% 90%, rgba(225,29,46,0.07), transparent 70%)',
        }}
      />
      <div className="container-x relative">
        {brokers.length > 1 && (
          <div className="mb-5 sm:mb-7 text-center">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)]">
              Recommended Brokers
            </div>
            <h2 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-none mt-1.5">
              Two regulated platforms. One winning playbook.
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto">
              Pick the platform that fits you best — both are tracked end-to-end so every signup
              counts toward your AlveeTrade rewards.
            </p>
          </div>
        )}

        <div className={'grid gap-5 lg:gap-6 ' + (brokers.length > 1 ? 'lg:grid-cols-2' : '')}>
          {brokers.map((b) => (
            <BrokerCard
              key={b.key}
              broker={b}
              single={brokers.length === 1}
              onClick={() => handleClick(b.sourceTag)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function BrokerCard({ broker, single, onClick }) {
  const accentColor = broker.accent === 'blue' ? 'hsl(212 80% 70%)' : 'hsl(43 92% 56%)';
  const accentBgSoft = broker.accent === 'blue' ? 'rgba(108,176,255,0.12)' : 'rgba(245,185,66,0.12)';
  const accentBorder = broker.accent === 'blue' ? 'rgba(108,176,255,0.32)' : 'rgba(245,185,66,0.32)';
  const lineGrad =
    broker.accent === 'blue'
      ? 'linear-gradient(90deg, transparent, rgba(108,176,255,0.65), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(245,185,66,0.7), transparent)';

  const stats = [
    broker.minDeposit && { Icon: Wallet, label: 'Min Deposit', value: broker.minDeposit },
    broker.payout && { Icon: Zap, label: 'Payout up to', value: broker.payout },
    broker.bonus && { Icon: Gift, label: 'Welcome', value: broker.bonus },
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5 }}
      className={
        'relative rounded-2xl card-surface overflow-hidden p-5 sm:p-7 lg:p-8 ' +
        (broker.accent === 'blue'
          ? 'border'
          : 'gold-hairline')
      }
      style={broker.accent === 'blue' ? { borderColor: accentBorder } : undefined}
      data-testid={`broker-card-${broker.key}`}
    >
      <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: lineGrad }} />
      <div className={'grid gap-5 lg:gap-7 items-center ' + (single ? 'lg:grid-cols-2' : '')}>
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: accentColor }}>
            {single ? 'Recommended Broker' : `Broker · ${broker.name}`}
          </div>
          <div className="flex items-center gap-3">
            {broker.logo ? (
              <img
                src={broker.logo}
                alt={broker.name}
                className="h-10 w-10 object-contain rounded-lg bg-white/5 p-1"
              />
            ) : (
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{ background: accentBgSoft, border: `1px solid ${accentBorder}` }}
              >
                <ShieldCheck className="h-5 w-5" style={{ color: accentColor }} />
              </div>
            )}
            <h3 className="font-display uppercase text-2xl sm:text-3xl lg:text-4xl leading-none">
              {broker.name}
            </h3>
          </div>
          <p className="text-sm text-[hsl(0_0%_82%)] leading-relaxed">
            {broker.tagline}. Trade Forex, Crypto, Commodities & Fixed-Time on a regulated platform —
            same broker our top traders use daily.
          </p>

          {stats.length > 0 && (
            <div className="grid grid-cols-3 gap-2 pt-2">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className={
                    'rounded-xl card-surface p-3 text-center ' +
                    (broker.accent === 'blue' ? 'border' : 'gold-hairline')
                  }
                  style={broker.accent === 'blue' ? { borderColor: accentBorder } : undefined}
                >
                  <div className="flex items-center justify-center mb-1" style={{ color: accentColor }}>
                    <s.Icon className="h-4 w-4" />
                  </div>
                  <div className="font-display uppercase text-base sm:text-lg text-white leading-none">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col items-stretch sm:items-start gap-3">
          <a
            href={broker.redirectUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={onClick}
            data-testid={`broker-cta-${broker.key}`}
            className="cta-sheen border-0 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md text-white font-semibold text-base group"
          >
            {broker.ctaLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <ul className="space-y-1.5 text-sm text-[hsl(0_0%_85%)]">
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
              Regulated multi-asset platform
            </li>
            <li className="flex items-start gap-2">
              <Zap className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
              Fast deposits & withdrawals
            </li>
            <li className="flex items-start gap-2">
              <Gift className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accentColor }} />
              Bonuses for AlveeTrade members
            </li>
          </ul>
          <p className="text-[10px] text-muted-foreground leading-snug max-w-md">
            AlveeTrade may receive a commission if you sign up via this link. Trading involves risk.
            Only trade with capital you can afford to lose.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
